import { useState } from "react";
import { motion } from "framer-motion";
import { apiFetch } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CalendarCheck, CheckCircle2 } from "lucide-react";

const empty = { name: "", phone: "", email: "", date: "", time: "", guests: "2", notes: "" };

export default function Reservation({ content }) {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.date || !form.time) {
      toast.error("Completa nombre, teléfono, fecha y hora.");
      return;
    }
    setLoading(true);
    try {
      await apiFetch("/reservations", { method: "POST", body: form });
      setDone(true);
      toast.success("¡Reserva enviada! Te contactaremos para confirmar.");
      setForm(empty);
    } catch (err) {
      toast.error(err.message || "No se pudo enviar la reserva.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="reservar" className="relative py-24 md:py-36 bg-[#1a1a1a] text-[#f9f6f0] grain overflow-hidden">
      <div className="max-w-6xl mx-auto px-5 md:px-8 relative z-10 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <p className="text-[#da251d] uppercase tracking-[0.3em] text-xs font-semibold mb-4">Reserva tu mesa</p>
          <h2 className="font-display text-4xl md:text-6xl font-bold tracking-tight leading-none mb-6">
            Ven a disfrutar del Perú
          </h2>
          <p className="text-white/70 leading-relaxed max-w-md mb-8">
            Reserva en segundos y te confirmamos por teléfono o WhatsApp. Ideal para familias, cumpleaños y celebraciones.
          </p>
          <div className="space-y-3 text-white/70">
            <p className="flex items-center gap-3"><CalendarCheck size={18} className="text-[#da251d]" /> {content.hours}</p>
            <p className="flex items-center gap-3"><CheckCircle2 size={18} className="text-[#da251d]" /> {content.address}</p>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-[#f9f6f0] text-[#1a1a1a] p-6 md:p-8"
        >
          {done ? (
            <div className="text-center py-10" data-testid="reservation-success">
              <CheckCircle2 size={56} className="text-[#da251d] mx-auto mb-5" />
              <h3 className="font-display text-2xl font-bold mb-2">¡Reserva recibida!</h3>
              <p className="text-[#57534e]">Nos pondremos en contacto contigo para confirmar tu mesa.</p>
              <Button onClick={() => setDone(false)} variant="outline" className="mt-6 rounded-full">Hacer otra reserva</Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="reservation-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="r-name">Nombre *</Label>
                  <Input id="r-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5 bg-white" data-testid="reservation-name" />
                </div>
                <div>
                  <Label htmlFor="r-phone">Teléfono *</Label>
                  <Input id="r-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1.5 bg-white" data-testid="reservation-phone" />
                </div>
              </div>
              <div>
                <Label htmlFor="r-email">Correo</Label>
                <Input id="r-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5 bg-white" data-testid="reservation-email" />
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="r-date">Fecha *</Label>
                  <Input id="r-date" type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className="mt-1.5 bg-white" data-testid="reservation-date" />
                </div>
                <div>
                  <Label htmlFor="r-time">Hora *</Label>
                  <Input id="r-time" type="time" value={form.time} onChange={(e) => set("time", e.target.value)} className="mt-1.5 bg-white" data-testid="reservation-time" />
                </div>
                <div>
                  <Label htmlFor="r-guests">Personas</Label>
                  <Input id="r-guests" type="number" min="1" value={form.guests} onChange={(e) => set("guests", e.target.value)} className="mt-1.5 bg-white" data-testid="reservation-guests" />
                </div>
              </div>
              <div>
                <Label htmlFor="r-notes">Notas</Label>
                <Textarea id="r-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} className="mt-1.5 bg-white" rows={2} placeholder="Alergias, ocasión especial, etc." data-testid="reservation-notes" />
              </div>
              <Button type="submit" disabled={loading} data-testid="reservation-submit" className="w-full bg-[#da251d] hover:bg-[#b91c1c] text-white h-12 rounded-full font-semibold">
                {loading ? "Enviando..." : "Reservar mesa"}
              </Button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
